import { Box, Button, Flex, Text, Textarea } from "@chakra-ui/react";
import { AgentTaskMemoryClickPayload, AgentTaskProgress, useAgentTasks } from "@fencyai/react";
import { Fragment, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useChatHistory, type ChatMessage } from "../fency/useChatHistory";
import { FENCY_DEFAULT_MODEL } from "../fency/fencySetup";

type MemoryChatProps = {
  fetchClientToken: () => Promise<{ clientToken: string }>;
  documentName: string;
  emptyStateText: string;
  placeholderText: string;
  initialPrompt?: string;
  canSend?: boolean;
  sendDisabledHint?: string;
  onSourceClick?: (payload: AgentTaskMemoryClickPayload) => void;
};

export const MemoryChat = ({
  fetchClientToken,
  documentName,
  emptyStateText,
  placeholderText,
  initialPrompt,
  canSend = true,
  sendDisabledHint,
  onSourceClick,
}: MemoryChatProps) => {
  const { t } = useTranslation();
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const fetchToken = useCallback(
    async () => fetchClientToken(),
    [fetchClientToken]
  );

  const { createAgentTask, agentTasks } = useAgentTasks({
    fetchCreateAgentTaskClientToken: fetchToken,
  });

  const sendMessages = useCallback(
    async (messages: ChatMessage[]) => {
      const result = await createAgentTask({
        type: "MemoryChat",
        messages,
        model: FENCY_DEFAULT_MODEL,
        language: "no",
        memoryScanLimit: 3,
        chunkLimit: 20,
      });

      if (result.type === "success") {
        const response = result.response;
        if (response.taskType === "MemoryChat" && response.eventType === "Completed") {
          return response.response.textResponse;
        }
      }
      return null;
    },
    [createAgentTask],
  );

  const {
    userMessages,
    prompt,
    setPrompt,
    isLoading,
    handleSend,
  } = useChatHistory({
    documentName,
    sendMessages,
    initialPrompt,
  });

  useEffect(() => {
    if (userMessages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [userMessages, agentTasks]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSourceClickInternal = useCallback(
    async (payload: AgentTaskMemoryClickPayload) => {
      onSourceClick?.(payload);
    },
    [onSourceClick]
  );

  const isDisabled = isLoading || !canSend;

  return (
    <Flex flex={1} flexDirection="column" minWidth={0} minH={0} overflow="hidden">
      <Box
        ref={messagesContainerRef}
        flex={1}
        minHeight={0}
        overflowY="auto"
        py="4"
        px="2"
        display="flex"
        flexDirection="column"
        gap="3"
      >
        {userMessages.length === 0 && (
          <Flex
            flex={1}
            alignItems="center"
            justifyContent="center"
            color="smMuted"
            fontSize="sm"
            textAlign="center"
          >
            <Text>{emptyStateText}</Text>
          </Flex>
        )}
        {userMessages.map((content, i) => (
          <Fragment key={`msg-${i}`}>
            <Flex justifyContent="flex-end">
              <Box
                maxWidth="75%"
                px="4"
                py="2"
                borderRadius="16px 16px 4px 16px"
                backgroundColor="smPrimary"
                color="white"
                fontSize="sm"
                lineHeight="tall"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                {content}
              </Box>
            </Flex>
            {agentTasks[i] && (
              <Flex justifyContent="flex-start">
                <Box maxWidth="100%" width="100%">
                  <AgentTaskProgress
                    agentTask={agentTasks[i]}
                    onSourceClick={handleSourceClickInternal}
                  />
                </Box>
              </Flex>
            )}
          </Fragment>
        ))}
      </Box>

      <Flex
        pt="3"
        pb="4"
        borderTopWidth="1px"
        borderColor="smBorder"
        gap="2"
        alignItems="flex-end"
        flexShrink={0}
        flexDirection="column"
      >
        {!canSend && sendDisabledHint && (
          <Text fontSize="sm" color="smMuted" alignSelf="flex-start">
            {sendDisabledHint}
          </Text>
        )}
        <Flex gap="2" alignItems="flex-end" width="100%">
          <Textarea
            placeholder={placeholderText}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            flex={1}
            resize="none"
          />
          <Button
            onClick={handleSend}
            colorScheme="teal"
            flexShrink={0}
            height="56px"
            px="5"
            isDisabled={isDisabled}
          >
            {t("Send")}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
